import { useEffect, useState } from 'react';
import { adminApi, formatBytes, formatDate } from '../api';
import { CopyButton, Modal } from '../components';

interface Cluster {
  id: string;
  region: string;
  provider: string;
  state: string;
  isPrimary: boolean;
  healthStatus: string;
  databaseCount: number;
  storageBytes: number;
  monthlyCostUsd: number | null;
  vendor: string | null;
  plan: string | null;
  cpuCores: number | null;
  ramGb: number | null;
  diskGb: number | null;
  location: string | null;
  notes: string | null;
  unit: { usdPerCore: number | null; usdPerRamGb: number | null; usdPerDiskGb: number | null };
  role: string | null;
  publicIp: string | null;
  bootstrappedAt: string | null;
  connection: 'connected' | 'disconnected';
  lastHeartbeatAt: string | null;
}
interface Bootstrap {
  role: string;
  expiresAt: string;
  hosts: { gateway: string; provisioner: string };
  publicIp: string | null;
  dnsManaged: boolean;
  install: { curl: string; ssh: string | null };
  remove: { curl: string; ssh: string | null };
}

const STATES = ['active', 'draining', 'maintenance', 'disabled', 'provisioning'] as const;

/**
 * Fleet table: what each node is (vendor, plan, cores, RAM, disk, location, price) next to what it does
 * (databases, storage, health). Editable inline so a node bought by hand can be described in a minute.
 * The unit prices are what to compare vendors on before there is enough traffic for revenue per node.
 */
export function FleetSection({ tick }: { tick: number }) {
  const [rows, setRows] = useState<Cluster[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<Record<string, Partial<Cluster>>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const load = () =>
    adminApi<{ clusters: Cluster[] }>('/clusters')
      .then((d) => setRows(d.clusters))
      .catch((e: Error) => setError(e.message));
  useEffect(() => {
    void load();
  }, [tick]);
  if (error) return <p className="error">{error}</p>;
  if (!rows) return <p className="muted">Loading fleet…</p>;

  const field = (c: Cluster, k: keyof Cluster) =>
    edit[c.id] && k in edit[c.id]! ? (edit[c.id]![k] as string | number | null) : c[k];
  const set = (id: string, k: keyof Cluster, v: string) =>
    setEdit((e) => ({
      ...e,
      [id]: {
        ...e[id],
        [k]: v === '' ? null : ['cpuCores', 'ramGb', 'diskGb', 'monthlyCostUsd'].includes(k) ? Number(v) : v,
      },
    }));
  async function save(c: Cluster) {
    const patch = edit[c.id];
    if (!patch) return;
    setBusy(c.id);
    try {
      const { state, monthlyCostUsd, ...spec } = patch;
      await adminApi(`/clusters/${c.id}`, {
        method: 'PATCH',
        json: {
          ...(state !== undefined ? { state } : {}),
          ...(monthlyCostUsd !== undefined ? { monthlyCostUsd } : {}),
          ...(Object.keys(spec).length ? { spec } : {}),
        },
      });
      setEdit((e) => {
        const n = { ...e };
        delete n[c.id];
        return n;
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(null);
    }
  }
  const num = (v: number | null, suffix = '') => (v === null ? '' : `${v}${suffix}`);
  const [cmd, setCmd] = useState<{ cluster: Cluster; boot: Bootstrap } | null>(null);
  async function commands(c: Cluster) {
    setBusy(c.id);
    setError(null);
    try {
      const boot = await adminApi<Bootstrap>(`/clusters/${c.id}/bootstrap-token`, { method: 'POST' });
      setCmd({ cluster: c, boot });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(null);
    }
  }
  async function disconnect(c: Cluster) {
    if (
      !window.confirm(
        `Mark ${c.id} as disconnected? Its DNS records are removed and it stops receiving databases.`,
      )
    )
      return;
    setBusy(c.id);
    try {
      await adminApi(`/clusters/${c.id}/disconnect`, { method: 'POST' });
      await load();
    } finally {
      setBusy(null);
    }
  }
  const [add, setAdd] = useState({
    id: '',
    region: '',
    publicHost: '',
    vendor: '',
    plan: '',
    cores: '',
    ram: '',
    disk: '',
    location: '',
    cost: '',
    role: 'all',
    publicIp: '',
  });
  async function addNode() {
    setBusy('add');
    setError(null);
    try {
      await adminApi('/clusters', {
        method: 'POST',
        json: {
          id: add.id.trim(),
          region: add.region.trim(),
          publicHost: add.publicHost.trim(),
          monthlyCostUsd: add.cost ? Number(add.cost) : null,
          role: add.role,
          publicIp: add.publicIp.trim() || null,
          spec: {
            vendor: add.vendor || null,
            plan: add.plan || null,
            cpuCores: add.cores ? Number(add.cores) : null,
            ramGb: add.ram ? Number(add.ram) : null,
            diskGb: add.disk ? Number(add.disk) : null,
            location: add.location || null,
          },
        },
      });
      setAdd({
        id: '',
        region: '',
        publicHost: '',
        vendor: '',
        plan: '',
        cores: '',
        ram: '',
        disk: '',
        location: '',
        cost: '',
        role: 'all',
        publicIp: '',
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(null);
    }
  }
  async function remove(c: Cluster) {
    if (!window.confirm(`Remove ${c.id} from the fleet? The server itself is not touched.`)) return;
    setBusy(c.id);
    setError(null);
    try {
      await adminApi(`/clusters/${c.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(null);
    }
  }
  return (
    <div className="card">
      <div className="row between">
        <h2>Fleet</h2>
        <span className="muted" style={{ fontSize: 12 }}>
          Fill in what each node is; the controller and the economics compare vendors on these numbers.
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="fleet-table">
          <thead>
            <tr>
              <th>Node</th>
              <th>State</th>
              <th>Vendor</th>
              <th>Plan</th>
              <th>Cores</th>
              <th>RAM GB</th>
              <th>Disk GB</th>
              <th>Location</th>
              <th>$/month</th>
              <th>$/core · $/GB RAM · $/GB disk</th>
              <th>Load</th>
              <th>Box</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>
                  <strong>{c.id}</strong>
                  <div className="muted">
                    {c.region} {c.isPrimary ? '· primary' : ''} · {c.healthStatus}
                  </div>
                </td>
                <td>
                  <select
                    value={String(field(c, 'state'))}
                    onChange={(e) => set(c.id, 'state', e.target.value)}
                  >
                    {STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    value={String(field(c, 'vendor') ?? '')}
                    placeholder="servercheap"
                    onChange={(e) => set(c.id, 'vendor', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    value={String(field(c, 'plan') ?? '')}
                    placeholder="NVMe-KVM-16GB"
                    onChange={(e) => set(c.id, 'plan', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={num(field(c, 'cpuCores') as number | null)}
                    onChange={(e) => set(c.id, 'cpuCores', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={num(field(c, 'ramGb') as number | null)}
                    onChange={(e) => set(c.id, 'ramGb', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={num(field(c, 'diskGb') as number | null)}
                    onChange={(e) => set(c.id, 'diskGb', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    value={String(field(c, 'location') ?? '')}
                    placeholder="dal"
                    onChange={(e) => set(c.id, 'location', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    value={num(field(c, 'monthlyCostUsd') as number | null)}
                    onChange={(e) => set(c.id, 'monthlyCostUsd', e.target.value)}
                  />
                </td>
                <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                  {c.unit.usdPerCore === null
                    ? '—'
                    : `$${c.unit.usdPerCore} · $${c.unit.usdPerRamGb ?? '?'} · $${c.unit.usdPerDiskGb ?? '?'}`}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {c.databaseCount} dbs · {formatBytes(c.storageBytes)}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span className={`badge ${c.connection === 'connected' ? 'active' : 'failed'}`}>
                    {c.connection}
                  </span>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {c.publicIp ?? 'no box yet'}
                    {c.lastHeartbeatAt ? ` · seen ${formatDate(c.lastHeartbeatAt)}` : ''}
                  </div>
                  <button
                    className="secondary"
                    style={{ marginTop: 4 }}
                    disabled={busy === c.id}
                    onClick={() => void commands(c)}
                  >
                    SSH commands
                  </button>{' '}
                  {c.connection === 'connected' && c.publicIp && (
                    <button
                      className="secondary"
                      style={{ marginTop: 4 }}
                      disabled={busy === c.id}
                      onClick={() => void disconnect(c)}
                    >
                      Disconnect
                    </button>
                  )}
                </td>
                <td>
                  <button
                    className="secondary"
                    disabled={!edit[c.id] || busy === c.id}
                    onClick={() => void save(c)}
                  >
                    Save
                  </button>{' '}
                  {c.state === 'disabled' && c.databaseCount === 0 && !c.isPrimary && (
                    <button className="danger" disabled={busy === c.id} onClick={() => void remove(c)}>
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <details style={{ marginTop: 14 }}>
        <summary>Add a node</summary>
        <p className="muted" style={{ marginTop: 8 }}>
          A new region is just a node whose region id nobody used before (e.g. <code>eu-frankfurt</code>); it
          appears in the wizard once the node is set to <strong>active</strong>. The node starts as{' '}
          <em>provisioning</em>: bootstrap it, then flip the state here.
        </p>
        <div className="grid cols-3">
          <div className="field">
            <label>Cluster id</label>
            <input
              placeholder="clu_fra_001"
              value={add.id}
              onChange={(e) => setAdd({ ...add, id: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Region id</label>
            <input
              placeholder="eu-frankfurt"
              value={add.region}
              onChange={(e) => setAdd({ ...add, region: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Gateway hostname</label>
            <input
              placeholder="eu-frankfurt.pg.tallypg.com"
              value={add.publicHost}
              onChange={(e) => setAdd({ ...add, publicHost: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Vendor · plan</label>
            <div className="row">
              <input
                placeholder="ovh"
                value={add.vendor}
                onChange={(e) => setAdd({ ...add, vendor: e.target.value })}
              />
              <input
                placeholder="Rise-1"
                value={add.plan}
                onChange={(e) => setAdd({ ...add, plan: e.target.value })}
              />
            </div>
          </div>
          <div className="field">
            <label>Cores · RAM GB · Disk GB</label>
            <div className="row">
              <input
                type="number"
                placeholder="8"
                value={add.cores}
                onChange={(e) => setAdd({ ...add, cores: e.target.value })}
              />
              <input
                type="number"
                placeholder="32"
                value={add.ram}
                onChange={(e) => setAdd({ ...add, ram: e.target.value })}
              />
              <input
                type="number"
                placeholder="500"
                value={add.disk}
                onChange={(e) => setAdd({ ...add, disk: e.target.value })}
              />
            </div>
          </div>
          <div className="field">
            <label>Role · box IP (optional, for the ssh command)</label>
            <div className="row">
              <select value={add.role} onChange={(e) => setAdd({ ...add, role: e.target.value })}>
                <option value="all">all (single box)</option>
                <option value="edge">edge (gateway only)</option>
                <option value="data">data (PostgreSQL only)</option>
              </select>
              <input
                placeholder="203.0.113.10"
                value={add.publicIp}
                onChange={(e) => setAdd({ ...add, publicIp: e.target.value })}
              />
            </div>
          </div>
          <div className="field">
            <label>Location · $/month</label>
            <div className="row">
              <input
                placeholder="vin"
                value={add.location}
                onChange={(e) => setAdd({ ...add, location: e.target.value })}
              />
              <input
                type="number"
                step="0.01"
                placeholder="60"
                value={add.cost}
                onChange={(e) => setAdd({ ...add, cost: e.target.value })}
              />
            </div>
          </div>
        </div>
        <button
          disabled={busy === 'add' || !add.id || !add.region || !add.publicHost}
          onClick={() => void addNode()}
        >
          Add node
        </button>
      </details>
      {cmd && (
        <Modal title={`${cmd.cluster.id} · ${cmd.boot.role} node`} onClose={() => setCmd(null)}>
          <p className="muted">
            One-time token, valid until {formatDate(cmd.boot.expiresAt)}. Run on the box as root.
            {cmd.boot.dnsManaged
              ? ' DNS records are created automatically when the box reports in.'
              : ` DNS is manual: create A records ${cmd.boot.hosts.gateway} and ${cmd.boot.hosts.provisioner} → the box IP (DNS-only, TTL 300), or set CLOUDFLARE_DNS_TOKEN + CLOUDFLARE_ZONE_ID on the Worker.`}
          </p>
          <h3 style={{ fontSize: 14 }}>Install / connect</h3>
          <div className="row">
            <pre style={{ flex: 1 }}>{cmd.boot.install.ssh ?? cmd.boot.install.curl}</pre>
            <CopyButton text={cmd.boot.install.ssh ?? cmd.boot.install.curl} />
          </div>
          {!cmd.boot.install.ssh && (
            <p className="muted">
              No IP on file: ssh in yourself and paste the line, or add the IP to the node to get a one-line
              ssh command.
            </p>
          )}
          <h3 style={{ fontSize: 14, marginTop: 14 }}>Remove / disconnect</h3>
          <div className="row">
            <pre style={{ flex: 1 }}>{cmd.boot.remove.ssh ?? cmd.boot.remove.curl}</pre>
            <CopyButton text={cmd.boot.remove.ssh ?? cmd.boot.remove.curl} />
          </div>
          <p className="muted">
            Stops the stack, keeps the data directory, removes the DNS records and marks the node disconnected
            here. If the box is already gone, use <em>Disconnect</em> on the row instead.
          </p>
        </Modal>
      )}
      <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
        Add a node, then click <strong>SSH commands</strong> and run the install line on the fresh box (Debian
        or Ubuntu, as root). The box fetches its configuration, builds the stack, reports its IP, DNS records
        are created when a Cloudflare DNS token is configured, and the row turns <em>connected</em> on its
        first heartbeat. Set it to <strong>active</strong> after that.
        <br />
        Remove: set the node to <strong>draining</strong> (the controller or <code>fleet moves</code> empties
        it), then <strong>disabled</strong>, then Remove. The CLI does the same: <code>fleet register …</code>
        .
      </p>
    </div>
  );
}
